/**
 * Whole-day arithmetic on ISO dates (YYYY-MM-DD), for the plan timeline.
 *
 * Everything here goes through UTC epoch days — never a local `Date`. A local
 * `new Date(2026, 5, 2)` plus `setDate(getDate() + 1)` is not a reliable "+1
 * day": in zones whose DST transition falls at local midnight (America/Santiago,
 * Lord Howe, and several historical zones) the step lands on the same calendar
 * day or skips one. UTC has no transitions, so `day * MS_PER_DAY` is exact for
 * every date, and `toISOString()` is defined to emit UTC — the round trip is
 * lossless in every timezone. Neither CI nor Playwright pins `TZ`, so being
 * structurally timezone-independent is the only safe option.
 */

const MS_PER_DAY = 86_400_000;

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** ISO date → days since 1970-01-01 (UTC). Anything unparsable → null. */
export const isoToEpochDay = (isoDate: string | undefined): number | null => {
  if (!isoDate) return null;
  const parts = ISO_DATE.exec(isoDate);
  if (!parts) return null;
  const year = Number(parts[1]);
  const month = Number(parts[2]);
  const day = Number(parts[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const utc = Date.UTC(year, month - 1, day);
  if (Number.isNaN(utc)) return null;
  // Rejects overflow dates like 2026-02-31, which Date.UTC silently rolls over.
  if (new Date(utc).getUTCDate() !== day) return null;
  return utc / MS_PER_DAY;
};

/** Days since 1970-01-01 (UTC) → ISO date. */
export const epochDayToIso = (day: number): string =>
  new Date(Math.round(day) * MS_PER_DAY).toISOString().slice(0, 10);

/** ISO date shifted by whole days. Unparsable input is returned unchanged. */
export const addDaysIso = (isoDate: string, delta: number): string => {
  const day = isoToEpochDay(isoDate);
  return day === null ? isoDate : epochDayToIso(day + delta);
};

/**
 * Inclusive day count from start to end — "02 Jun → 13 Jun" is 12 days, which
 * is what a timeline bar's duration label means. An end before its start counts
 * as a single day; unparsable input also counts as one, so a bar is never
 * zero-width.
 */
export const dayCountBetween = (startIso: string, endIso: string): number => {
  const start = isoToEpochDay(startIso);
  const end = isoToEpochDay(endIso);
  if (start === null || end === null) return 1;
  return Math.max(1, end - start + 1);
};
